import React, { useCallback, useMemo, useRef, useState } from 'react'
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table'
import { useInfiniteQuery } from '@tanstack/react-query'
import { DivisionId, GoalId, GoalWithProgress } from '../../services/api.types'
import apiUrl from '../../services/apiUrl'
import ApiClient from '../../services/ApiClient'
import { msgid, ngettext, t } from 'ttag'
import { formatThousands } from '../../common/helpers/translations'
import { isDistanceType, isPersistenceType } from '../../common/helpers/challenges'
import { getCustomUnitText } from '../../services/CustomTypes'
import Loader from '../Loader/Loader'
import { borderGray } from '../../styles/material-ui-theme-v2'
import { Box } from '@material-ui/core'
import YumDialog from '../YumDialog/YumDialog'
import YumDialogTitle from '../YumDialogTitle/YumDialogTitle'
import YumSearchField from '../YumSearchField/YumSearchField'

type LeaderboardProgress = {
  userId: string
  rank: number
  name: string
  teamId: DivisionId
  teamName: string
  country: string
  result: number
  percentage: number
  data: string | null
  participants?: number
}

type GetPeopleLeaderboardProgress$Response = {
  data: Array<LeaderboardProgress>
  totalRowCount: number
  ofPeriodPassedPercentage: number
}

const membersFetchSize = 50

interface LeaderboardMembersModalProps {
  open: boolean
  onClose: () => void
  goalId: GoalId
  goalDto: GoalWithProgress
  teamId: DivisionId
  teamName: string
  country?: string
  token?: string
  showContributions?: boolean
}

const LeaderboardMembersModal = ({
  open,
  onClose,
  goalId,
  goalDto,
  teamId,
  teamName,
  country,
  token,
  showContributions = true,
}: LeaderboardMembersModalProps) => {
  const [search, setSearch] = useState('')
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isFetching } = useInfiniteQuery(
    ['team-members-modal', goalId, teamId, country, search],
    async ({ pageParam = 0 }) => {
      const response = (await ApiClient.post(apiUrl('/rpc/getPeopleLeaderboardProgress'), {
        goalId,
        countries: country ? [country] : [],
        divisionIds: country ? [] : [teamId],
        token,
        start: pageParam * membersFetchSize,
        size: membersFetchSize,
        search,
      })) as GetPeopleLeaderboardProgress$Response
      return response || { data: [], totalRowCount: 0 }
    },
    {
      enabled: open,
      cacheTime: 0,
      getNextPageParam: (lastPage, allPages) => {
        const fetched = allPages.reduce((sum, page) => sum + (page.data?.length || 0), 0)
        return fetched < (lastPage.totalRowCount || 0) ? allPages.length : undefined
      },
    },
  )

  const members = useMemo(() => data?.pages?.flatMap((page) => page.data || []) || [], [data])
  const totalRowCount = data?.pages?.[0]?.totalRowCount ?? 0

  const customType = goalDto.customType
  const customUnit = goalDto.customUnit
  const progressType = goalDto.progressType
  const isPersistence = progressType ? isPersistenceType(progressType) : false

  const columns = useMemo<MRT_ColumnDef<LeaderboardProgress>[]>(
    () => [
      {
        accessorKey: 'rank',
        header: 'Rank',
        size: 50,
        Cell: ({ cell }) => {
          const { rank } = cell.row.original
          return <span>{rank}.</span>
        },
        enableGlobalFilter: false,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ cell }) => {
          const original = cell.row.original
          return (
            <span>
              {original.name}
              {/*<span style={{ display: 'inline-block', verticalAlign: 'middle', overflow: 'hidden' }}>
                {original.name}
              </span>*/}
            </span>
          )
        },
      },
      ...(showContributions
        ? [
            {
              accessorKey: 'percentage' as const,
              header: 'Result',
              Cell: ({ cell }: any) => {
                const { result, percentage = 0 } = cell.row.original
                const unit = isPersistence
                  ? ngettext(msgid`day`, `days`, result || 0)
                  : (getCustomUnitText(customType, customType, customUnit) as string)
                const isDistance = !isPersistence && customType ? isDistanceType(customType) : false
                return (
                  <span style={{ fontWeight: 'bold' }}>
                    {formatThousands(result || 0, isDistance ? 2 : undefined)} {unit}
                  </span>
                )
              },
              enableGlobalFilter: false,
            },
          ]
        : []),
    ],
    [customType, customUnit, isPersistence, showContributions],
  )

  const fetchMoreOnBottomReached = useCallback(
    (containerRef: HTMLDivElement | null) => {
      if (containerRef) {
        const { scrollHeight, scrollTop, clientHeight } = containerRef
        if (scrollHeight - scrollTop - clientHeight < 200 && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  return (
    <YumDialog
      open={open}
      onClose={onClose}
      PaperProps={{ style: { width: '500px', maxWidth: '100%', paddingBottom: 0 } }}
    >
      <YumDialogTitle handleClose={onClose}>
        {teamName}
        {totalRowCount > 0 && <span style={{ color: '#24284e80', fontWeight: 'normal' }}> ({totalRowCount})</span>}
      </YumDialogTitle>
      <Box sx={{ padding: '0 24px 8px 24px' }}>
        <YumSearchField value={search} onChange={setSearch} autoFocus={false} />
      </Box>
      {isLoading || (isFetching && members.length === 0) ? (
        <Box
          sx={{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}
        >
          <Loader noPadding />
        </Box>
      ) : (
        <Box
          ref={tableContainerRef}
          onScroll={() => fetchMoreOnBottomReached(tableContainerRef.current)}
          sx={{ maxHeight: '60vh', overflowY: 'auto' }}
        >
          <MaterialReactTable<LeaderboardProgress>
            columns={columns}
            data={members}
            enablePagination={false}
            enableRowNumbers={false}
            enableFilterMatchHighlighting={false}
            enableColumnFilters={false}
            enableGlobalFilter={false}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            enableColumnActions={false}
            enableSorting={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            layoutMode="grid"
            state={{ showSkeletons: false, showProgressBars: false }}
            rowCount={totalRowCount}
            localization={{
              noRecordsToDisplay: t`No matches found.`,
            }}
            muiTablePaperProps={{
              elevation: 0,
              sx: {
                backgroundColor: 'transparent',
                '& .progress-cell': { width: '100%', maxWidth: 'none' },
                '& .progress-cell__value': { marginBottom: 0 },
              },
            }}
            muiTableHeadProps={{ sx: { display: 'none' } }}
            muiTableContainerProps={{
              sx: { paddingBottom: '16px' },
            }}
            muiTableBodyCellProps={({ cell }) => ({
              sx: {
                fontFamily: 'inherit',
                fontSize: 'inherit',
                borderBottom: `1px solid ${borderGray}`,
                backgroundColor: 'transparent',
                lineHeight: 'normal',
                padding: '10px 8px',
                'tr:last-child > &': { borderBottomColor: 'transparent' },
                ...(cell.column.id === 'rank' && {
                  //width: '24px',
                  minWidth: '58px',
                  //maxWidth: '24px',
                  maxWidth: 'auto',
                  paddingLeft: '24px',
                  paddingRight: '4px',
                  flexGrow: 0,
                  flexShrink: 0,
                  opacity: 0.6,
                  //justifyContent: 'flex-end',
                  justifyContent: 'flex-start',
                }),
                ...(cell.column.id === 'name' && {
                  minWidth: 0,
                  width: 'auto',
                  flexGrow: 1,
                  flexShrink: 1,
                  flexBasis: '100%',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  paddingLeft: '8px',
                  '& > span': { width: '100%', display: 'block' },
                }),
                ...(cell.column.id === 'percentage' && {
                  width: 'auto',
                  minWidth: '80px',
                  paddingRight: '24px',
                  justifyContent: 'flex-end',
                  flexGrow: 0,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  textAlign: 'right' as const,
                  overflow: 'visible',
                }),
              },
            })}
            muiTableBodyRowProps={{
              hover: false,
              sx: { '&:hover td': { backgroundColor: 'transparent !important' } },
            }}
            muiTableBodyCellSkeletonProps={{ sx: { display: 'none' } }}
            renderEmptyRowsFallback={() =>
              !isLoading ? (
                <Box
                  sx={{
                    padding: '24px 16px',
                    textAlign: 'center',
                    color: '#24284e80',
                    fontSize: '14px',
                    minHeight: '98px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >{t`No matches found.`}</Box>
              ) : null
            }
          />
          {isFetchingNextPage && (
            <Box sx={{ padding: '12px', display: 'flex', justifyContent: 'center' }}>
              <Loader noPadding />
            </Box>
          )}
        </Box>
      )}
    </YumDialog>
  )
}

export default LeaderboardMembersModal
